/*
  Resty HK biological age test (sections/resty-hk-bio-age.liquid), ported from the feelresty.com test of 2026-10-29.

  Everything is calculated in the browser: the answers go nowhere. The analytics events below carry no answer and no result, only that the
  test started or finished.

  Method: each habit has a hazard ratio (HR) for mortality from a large study, relative to the reference answer. In adults that risk roughly
  doubles every 8 years of age (Gompertz), so an HR equals 8 x log2(HR) years (the "rate advancement period", Brenner 1993). The sum added to
  the age is the lifestyle age. The source of every factor is in snippets/resty-hk-bio-age-sources.liquid.
*/
(() => {
  const MRDT = 8;
  const CAP = [-12, 20];

  // HR per answer; the reference answer counts as 0 years. Each factor is centred on a typical answer, otherwise an ordinary visitor comes
  // out too young (the studies often take the worst group as their reference). Numbers and sources read on 2026-10-29 for the feelresty.com
  // test (see snippets/resty-hk-bio-age-sources.liquid and docs/HK-LAUNCH.md, "Free tools"); keep them equal to the European test.
  const MODEL = {
    // Yin 2017: 4 h 1.08, 5 h 1.04, 6 h 1.01, 7 h 1, 8 h 1.04, 9 h 1.15, 10 h 1.32 (category midpoints; 6-7 and 7-8 hours are both within 2%
    // of the minimum).
    sleep_hours: { ref: 'h7', hr: { lt5: 1.06, h5: 1.025, h6: 1, h7: 1, h8: 1.095, ge9: 1.235 } },
    // Windred 2024 (Sleep), quintiles centred 1.23/1.02/0.96/0.92/0.91; the spread of bedtimes stands in for measured regularity.
    sleep_regularity: { ref: 'm60', hr: { m30: 0.91, m60: 0.94, h2: 1.02, gt2: 1.23 } },
    // Windred 2024 (PNAS), model 3 centred, night 0.95/0.96/1.10/1.15.
    night_light: { ref: 'some', hr: { dark: 0.95, some: 1.03, light: 1.15 } },
    // Same study, day 1.08/0.97/0.91/0.89; time outside stands in for measured light.
    daylight: { ref: 'm60', hr: { lt15: 1.1, m30: 1.06, m60: 1, h2: 0.95, gt2: 0.9 } },
    // Arem 2015, relative to none: <1x the guideline 0.80, 1-2x 0.69, 2-3x 0.63, 3-5x 0.61.
    activity: { ref: 'm150', hr: { none: 1, lt75: 0.84, m150: 0.78, m300: 0.69, gt300: 0.62 } },
    // Patterson 2018: 1.01 per hour up to 8 hours, 1.04 per hour above.
    sitting: { ref: 'h8', hr: { lt4: 0.961, h6: 0.98, h8: 1, h10: 1.05, gt10: 1.136 } },
    // Jha 2013 and Cho 2024 (a day 2.7-3.0), Inoue-Choi 2017 (less than one a day 1.64), Cho 2024 (quit 10+ years ago almost as never-smokers).
    smoking: { ref: 'never', hr: { never: 1, quit_old: 1.1, quit_new: 1.5, some: 1.64, daily: 2.8 } },
    // Wood 2018 (read from the figure): ~1 up to 100 g a week, ~1.2 at 250-350 g, ~1.46 above; one drink is 10 g. Not drinking counts as no gain.
    alcohol: { ref: 'w7', hr: { none: 1, w7: 1, w14: 1.03, w21: 1.1, gt21: 1.3 } },
    // Wang 2021, relative to ~2 portions: 3.3 0.95, 4.2 0.89, 5+ 0.88.
    produce: { ref: 'p2', hr: { p1: 1.05, p2: 1, p4: 0.92, p5: 0.88 } },
    // Global BMI Mortality Collaboration 2016 (never-smokers, reference 22.5-25).
    bmi: { ref: 'normal', hr: { under: 1.51, normal: 1, over: 1.135, obese: 1.45, severe: 2 } },
    // Wang 2023: social isolation 1.32, loneliness 1.14.
    social: { ref: 'rare', hr: { rare: 1, sometimes: 1.07, often: 1.2 } },
  };

  const years = (hr, ref) => MRDT * Math.log2(hr / ref);
  const bmiCategory = (bmi) =>
    bmi < 18.5 ? 'under' : bmi < 25 ? 'normal' : bmi < 30 ? 'over' : bmi < 35 ? 'obese' : 'severe';

  class RestyHkBioAge extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      // The t filter escapes text for HTML (an apostrophe becomes &#39;); here the text reaches the page through textContent, so decode it.
      const config = this.querySelector('[data-rba-config]');
      const decode = (value) => {
        const area = document.createElement('textarea');
        area.innerHTML = value;
        return area.value;
      };
      this.t = Object.fromEntries(Object.entries(config ? JSON.parse(config.textContent) : {}).map(([key, value]) => [key, decode(value)]));
      this.locale = this.dataset.locale || document.documentElement.lang || 'en';
      this.number = new Intl.NumberFormat(this.locale, { maximumFractionDigits: 1, minimumFractionDigits: 1 });
      this.plurals = new Intl.PluralRules(this.locale);
      this.form = this.querySelector('[data-rba-form]');
      this.steps = [...this.querySelectorAll('[data-rba-step]')];
      this.result = this.querySelector('[data-rba-result]');
      this.answers = {};
      this.index = 0;

      this.form.addEventListener('submit', (event) => event.preventDefault());
      this.form.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('click', (event) => this.onClick(event));
      this.form.hidden = false;
      this.querySelector('[data-rba-noscript]')?.remove();
      this.show(0, false);
    }

    format(template, values) {
      return Object.entries(values).reduce((text, [key, value]) => text.split(`[${key}]`).join(value), template || '');
    }

    yearsText(value, signed) {
      const abs = Math.abs(value);
      const rule = this.plurals.select(Number(abs.toFixed(1)) === Math.round(abs) ? Math.round(abs) : abs);
      const template = this.t[`years_${rule}`] || this.t.years_other;
      const shown = Number.isInteger(value) ? String(abs) : this.number.format(abs);
      const sign = signed ? (value > 0.05 ? '+' : value < -0.05 ? '−' : '') : '';
      return sign + this.format(template, { n: shown });
    }

    track(name, params) {
      try {
        window.Shopify?.analytics?.publish?.(name, { tool: 'bio_age', tool_locale: this.locale, ...params });
      } catch (error) {}
    }

    show(index, focus = true) {
      clearTimeout(this.advance);
      this.index = index;
      this.steps.forEach((step, i) => {
        step.hidden = i !== index;
      });
      const total = this.steps.length;
      const bar = this.querySelector('[data-rba-bar]');
      if (bar) bar.style.transform = `scaleX(${(index + 1) / total})`;
      const count = this.querySelector('[data-rba-count]');
      if (count) count.textContent = this.format(this.t.progress, { current: index + 1, total });
      const back = this.querySelector('[data-rba-back]');
      if (back) back.hidden = index === 0;
      const step = this.steps[index];
      // A choice question advances on change; after Back its answer is still checked, so Continue stays available there.
      this.querySelector('[data-rba-next]').hidden = !step.hasAttribute('data-rba-manual') && !this.answers[step.dataset.factor];
      this.querySelector('[data-rba-skip]').hidden = !step.hasAttribute('data-rba-optional');
      this.error('');
      if (focus) {
        const legend = step.querySelector('legend');
        legend?.setAttribute('tabindex', '-1');
        legend?.focus({ preventScroll: true });
        const top = this.form.getBoundingClientRect().top;
        if (top < 0 || top > window.innerHeight * 0.6) this.form.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    error(text) {
      const node = this.querySelector('[data-rba-error]');
      if (!node) return;
      node.textContent = text;
      node.hidden = !text;
    }

    onChange(event) {
      const input = event.target;
      if (input.type !== 'radio') return;
      const factor = input.closest('[data-rba-step]')?.dataset.factor;
      if (!factor) return;
      if (!this.started) {
        this.started = true;
        this.track('resty_tool_start', {});
      }
      this.answers[factor] = input.value;
      clearTimeout(this.advance);
      this.advance = setTimeout(() => this.next(), 260);
    }

    onClick(event) {
      const target = event.target.closest('button, a');
      if (!target) return;
      if (target.matches('[data-rba-next]')) this.next();
      else if (target.matches('[data-rba-back]')) this.show(Math.max(0, this.index - 1));
      else if (target.matches('[data-rba-skip]')) {
        this.answers.bmi = null;
        this.next(true);
      } else if (target.matches('[data-rba-restart]')) this.restart();
      else if (target.matches('[data-rba-share]')) this.share(target);
    }

    readManual(step) {
      if (step.dataset.factor === 'age') {
        const age = Number(step.querySelector('input').value);
        if (!(age >= 18 && age <= 90)) return this.error(this.t.age_error), false;
        if (!this.started) {
          this.started = true;
          this.track('resty_tool_start', {});
        }
        this.age = Math.round(age);
        return true;
      }
      if (step.dataset.factor === 'bmi') {
        const [height, weight] = [...step.querySelectorAll('input')].map((field) => Number(field.value));
        if (!(height >= 120 && height <= 220 && weight >= 35 && weight <= 250)) return this.error(this.t.bmi_error), false;
        this.answers.bmi = bmiCategory(weight / (height / 100) ** 2);
        return true;
      }
      return true;
    }

    next(skipped = false) {
      const step = this.steps[this.index];
      if (!skipped && step.hasAttribute('data-rba-manual') && !this.readManual(step)) return;
      if (!skipped && !step.hasAttribute('data-rba-manual') && !this.answers[step.dataset.factor]) return;
      if (this.index < this.steps.length - 1) this.show(this.index + 1);
      else this.finish();
    }

    deltas() {
      return Object.keys(MODEL)
        .filter((factor) => this.answers[factor])
        .map((factor) => {
          const { ref, hr } = MODEL[factor];
          const value = years(hr[this.answers[factor]], hr[ref]);
          const best = Math.min(...Object.values(hr).map((h) => years(h, hr[ref])));
          return { factor, value, gain: value - best };
        });
    }

    finish() {
      this.form.hidden = true;
      this.result.hidden = false;
      this.render();
      this.track('resty_tool_result', { tool_steps: this.steps.length });
      this.result.querySelector('[data-rba-age]')?.focus({ preventScroll: true });
      this.result.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    render() {
      const rows = this.deltas();
      const total = Math.min(CAP[1], Math.max(CAP[0], rows.reduce((sum, row) => sum + row.value, 0)));
      const lifestyle = Math.round(this.age + total);
      const diff = lifestyle - this.age;

      this.result.querySelector('[data-rba-age]').textContent = lifestyle;
      const compare = diff < 0 ? this.t.younger : diff > 0 ? this.t.older : this.t.same;
      this.result.querySelector('[data-rba-compare]').textContent = this.format(compare, {
        years: this.yearsText(Math.abs(diff)),
        age: this.age,
      });

      // Scale: the real age and the lifestyle age on one line, with a margin of 15 years.
      const span = Math.max(15, Math.abs(diff) + 5);
      const place = (value) => `${((value - (this.age - span)) / (span * 2)) * 100}%`;
      this.result.querySelector('[data-rba-mark-age]').style.left = place(this.age);
      this.result.querySelector('[data-rba-mark-life]').style.left = place(lifestyle);
      this.result.querySelector('[data-rba-scale-min]').textContent = this.age - span;
      this.result.querySelector('[data-rba-scale-max]').textContent = this.age + span;

      const maxAbs = Math.max(2, ...rows.map((row) => Math.abs(row.value)));
      const list = this.result.querySelector('[data-rba-rows]');
      list.replaceChildren(
        ...rows.map((row) => {
          const item = document.createElement('li');
          item.className = 'rba-row';
          const id = `${this.id || 'rba'}-whatif-${row.factor}`;
          const label = document.createElement('label');
          label.className = 'rba-row__label';
          label.htmlFor = id;
          label.textContent = this.t[`f_${row.factor}`] || row.factor;
          const select = document.createElement('select');
          select.className = 'rba-row__select';
          select.id = id;
          select.dataset.factor = row.factor;
          this.optionsFor(row.factor).forEach(([value, text]) => select.add(new Option(text, value, false, value === this.answers[row.factor])));
          select.addEventListener('change', () => {
            this.answers[row.factor] = select.value;
            this.render();
            this.result.querySelector(`#${CSS.escape(id)}`)?.focus();
          });
          const bar = document.createElement('span');
          bar.className = 'rba-row__bar';
          const fill = document.createElement('i');
          fill.className = row.value > 0.05 ? 'is-older' : row.value < -0.05 ? 'is-younger' : 'is-zero';
          fill.style.width = `${(Math.abs(row.value) / maxAbs) * 50}%`;
          bar.append(fill);
          const value = document.createElement('span');
          value.className = 'rba-row__value';
          value.textContent = Math.abs(row.value) < 0.05 ? this.t.zero : this.yearsText(Number(row.value.toFixed(1)), true);
          item.append(label, select, bar, value);
          return item;
        }),
      );

      const levers = rows.filter((row) => row.gain >= 0.3).sort((a, b) => b.gain - a.gain).slice(0, 3);
      const leverList = this.result.querySelector('[data-rba-levers]');
      leverList.replaceChildren(
        ...levers.map((row, i) => {
          const item = document.createElement('li');
          item.className = 'rba-lever';
          const number = document.createElement('span');
          number.className = 'rba-lever__n';
          number.textContent = i + 1;
          const title = document.createElement('h4');
          title.textContent = this.t[`f_${row.factor}`];
          const gain = document.createElement('p');
          gain.className = 'rba-lever__gain';
          gain.textContent = this.format(this.t.lever_gain, { years: this.yearsText(Number(row.gain.toFixed(1))) });
          const tip = document.createElement('p');
          tip.textContent = this.t[`tip_${row.factor}`];
          item.append(number, title, gain, tip);
          return item;
        }),
      );
      this.result.querySelector('[data-rba-levers-empty]').hidden = levers.length > 0;
    }

    optionsFor(factor) {
      if (factor === 'bmi') {
        return Object.keys(MODEL.bmi.hr).map((key) => [key, this.t[`bmi_${key}`] || key]);
      }
      const step = this.steps.find((node) => node.dataset.factor === factor);
      return [...step.querySelectorAll('input[type="radio"]')].map((input) => [
        input.value,
        input.closest('label').querySelector('[data-rba-option-text]').textContent.trim(),
      ]);
    }

    restart() {
      this.answers = {};
      this.form.reset();
      this.result.hidden = true;
      this.form.hidden = false;
      this.show(0);
    }

    async share(button) {
      const url = this.dataset.shareUrl || window.location.href.split('#')[0];
      const text = this.t.share_text;
      try {
        if (navigator.share) {
          await navigator.share({ title: document.title, text, url });
          return;
        }
        await navigator.clipboard.writeText(url);
        const label = button.textContent;
        button.textContent = this.t.copied;
        setTimeout(() => {
          button.textContent = label;
        }, 2200);
      } catch (error) {}
    }
  }

  if (!customElements.get('resty-hk-bio-age')) customElements.define('resty-hk-bio-age', RestyHkBioAge);
})();
